/**
 * Helpers for the inline Chili Piper slot picker.
 * API times are always stored as UTC ISO-8601 strings.
 * The UI groups and formats them by a user-selected IANA timezone.
 */

export interface BookingTimezoneOption {
  readonly value: string;
  readonly label: string;
}

const FALLBACK_TZ       = 'Europe/Berlin';
const FALLBACK_TZ_LABEL = 'Dubrovnik local time';

/** Fallback IANA timezone for an executive's slots when the admin portal has
 *  none configured for them. Mirrors pcheck_agent.exec_registry.DEFAULT_TIMEZONE
 *  (PST/PDT) — keep these in sync. */
export const EXEC_DEFAULT_TIMEZONE = 'America/Los_Angeles';

/** Resolves the IANA timezone an executive's slots should render in:
 *  their admin-configured zone, or EXEC_DEFAULT_TIMEZONE when unset. */
export function resolveExecTimezone(exec: { timezone?: string }): string {
  return exec.timezone && exec.timezone.trim() ? exec.timezone : EXEC_DEFAULT_TIMEZONE;
}

/**
 * Parses an IANA timezone identifier from a config string like
 * "CET/Dubrovnik — Europe/Amsterdam". Falls back to Europe/Berlin.
 */
export function parseIanaFromConfigValue(raw: string | null | undefined): string {
  if (!raw) return FALLBACK_TZ;
  const parts = raw.split(/\s[—\-]\s/);
  const iana  = parts[parts.length - 1]?.trim();
  return iana && iana.includes('/') ? iana : FALLBACK_TZ;
}

/** Single fixed timezone option — all meetings display in Central European time. */
export const BOOKING_DISPLAY_TIMEZONES: readonly BookingTimezoneOption[] = [
  { value: FALLBACK_TZ, label: FALLBACK_TZ_LABEL },
] as const;

export function defaultBookingDisplayTimezone(): string {
  return FALLBACK_TZ;
}

export function buildBookingTimezoneOptions(
  iana  = FALLBACK_TZ,
  label = FALLBACK_TZ_LABEL,
): BookingTimezoneOption[] {
  return [{ value: iana, label }];
}

/** Calendar date YYYY-MM-DD for `instant` in `timeZone`. */
export function dateKeyInTimeZone(instant: Date, timeZone: string): string {
  return instant.toLocaleDateString('en-CA', { timeZone });
}

/** Add `n` civil days to a YYYY-MM-DD string. DST-safe via UTC math. */
export function addCalendarDaysYmd(ymd: string, n: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return [
    dt.getUTCFullYear(),
    String(dt.getUTCMonth() + 1).padStart(2, '0'),
    String(dt.getUTCDate()).padStart(2, '0'),
  ].join('-');
}

/** Returns any instant that falls on civil date `ymd` in `timeZone`. */
export function firstInstantOnZonedDate(ymd: string, timeZone: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  let t = Date.UTC(y, m - 1, d - 1, 0, 0, 0);
  for (let i = 0; i < 96; i++) {
    t += 3_600_000;
    if (dateKeyInTimeZone(new Date(t), timeZone) === ymd) return new Date(t);
  }
  return new Date(Date.UTC(y, m - 1, d, 8, 0, 0));
}

/** Format YYYY-MM-DD as "Apr 26" — locale-stable, TZ-independent. */
export function formatYmdShort(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC', month: 'short', day: 'numeric',
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

/** Calendar-day count between two YYYY-MM-DD keys (to - from). */
export function daysBetweenYmd(from: string, to: string): number {
  const [fy, fm, fd] = from.split('-').map(Number);
  const [ty, tm, td] = to.split('-').map(Number);
  return Math.round(
    (Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000,
  );
}

/** Format an ISO-UTC slot time as "2:30 PM" in the given timezone. */
export function formatSlotTimeInZone(isoUtc: string, timeZone: string): string {
  return new Intl.DateTimeFormat(undefined, {
    timeZone, hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(new Date(isoUtc));
}

/** Format an ISO-UTC slot time as "Jun 18, 2:30 PM" in the given timezone. */
export function formatSlotDateTimeInZone(isoUtc: string, timeZone: string): string {
  return new Intl.DateTimeFormat(undefined, {
    timeZone, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(new Date(isoUtc));
}

/** Format an ISO-UTC instant as full locale date+time string in the given timezone. */
export function formatZonedDateTimeLine(isoUtc: string, timeZone: string): string {
  return new Date(isoUtc).toLocaleString('en-US', {
    timeZone,
    weekday: 'short', month: 'short', day: 'numeric',
    year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

// ── Local-timezone display (management flows + booking picker) ────────────────
// Meeting times are stored/sent as UTC ISO-8601 and rendered in a user-chosen
// IANA timezone, defaulting to the browser's local zone.

/** The browser's IANA timezone (e.g. "Asia/Kolkata"); falls back to UTC. */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** True when `value` looks like a UTC ISO-8601 instant (e.g. 2026-05-06T03:30:00Z).
 *  Used to tell the new UTC-ISO backend payloads apart from pre-formatted
 *  legacy strings (IMPACT still sends a formatted "6 May 2026 11:30 …" string). */
export function isIsoUtc(value: string | null | undefined): boolean {
  if (!value) return false;
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value.trim());
}

let _ALL_TZ_CACHE: string[] | null = null;

/** Every IANA timezone the runtime knows (Intl.supportedValuesOf), with a
 *  small hardcoded fallback for older engines. Cached after first call. */
export function allTimezones(): string[] {
  if (_ALL_TZ_CACHE) return _ALL_TZ_CACHE;
  let zones: string[] = [];
  try {
    const sv = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
    if (typeof sv === 'function') zones = sv('timeZone') ?? [];
  } catch { /* ignore */ }
  if (!zones.length) {
    zones = [
      'UTC', 'America/Los_Angeles', 'America/Denver', 'America/Chicago',
      'America/New_York', 'America/Sao_Paulo', 'Europe/London', 'Europe/Berlin',
      'Europe/Paris', 'Europe/Madrid', 'Africa/Johannesburg', 'Asia/Dubai',
      'Asia/Kolkata', 'Asia/Bangkok', 'Asia/Makassar', 'Asia/Singapore',
      'Asia/Shanghai', 'Asia/Tokyo', 'Australia/Sydney', 'Pacific/Auckland',
    ];
  }
  // Ensure the browser zone is present and listed first.
  const local = browserTimezone();
  const rest = zones.filter(z => z !== local).sort((a, b) => a.localeCompare(b));
  _ALL_TZ_CACHE = zones.includes(local) ? [local, ...rest] : [local, ...rest];
  return _ALL_TZ_CACHE;
}

/** Human label for a zone, e.g. "Asia/Kolkata (GMT+5:30)". TZ-name + current offset. */
export function timezoneLabel(iana: string): string {
  try {
    const offset = new Intl.DateTimeFormat('en-US', {
      timeZone: iana, timeZoneName: 'shortOffset',
    }).formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value ?? '';
    const pretty = iana.replace(/_/g, ' ');
    return offset ? `${pretty} (${offset})` : pretty;
  } catch {
    return iana.replace(/_/g, ' ');
  }
}

/**
 * Returns a human-readable timezone abbreviation for display (e.g. "CET", "Dubrovnik").
 * Resolution: config label > Intl abbreviation > hard fallback.
 */
export function getNamedTimezoneAbbr(ianaTimezone: string, configLabel: string): string {
  const fromLabel = _abbrFromLabel(configLabel, ianaTimezone);
  if (fromLabel) return fromLabel;
  try {
    const abbr = new Intl.DateTimeFormat('en-US', {
      timeZone: ianaTimezone, timeZoneName: 'short',
    }).formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value ?? '';
    if (abbr && !/^(GMT|UTC)[+-]/.test(abbr)) return abbr;
  } catch { /* ignore */ }
  return 'Dubrovnik local time';
}

/** Short zone label for the exec-picker slot cards, e.g. "PST" / "PDT".
 *  Unlike getNamedTimezoneAbbr, falls back to the IANA id itself (never the
 *  Dubrovnik placeholder) since this always describes a real executive zone. */
export function execTimezoneAbbr(ianaTimezone: string): string {
  try {
    const abbr = new Intl.DateTimeFormat('en-US', {
      timeZone: ianaTimezone, timeZoneName: 'short',
    }).formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value ?? '';
    if (abbr && !/^(GMT|UTC)[+-]/.test(abbr)) return abbr;
  } catch { /* ignore */ }
  return ianaTimezone.replace(/_/g, ' ');
}

function _abbrFromLabel(label: string, ianaTimezone: string): string {
  if (!label) return '';
  const abbrPart = label.split(/\s[—\-]\s/)[0]?.trim() ?? '';
  if (abbrPart && !/\s/.test(abbrPart)) {
    if (abbrPart.includes('/')) {
      const [std, dst] = abbrPart.split('/');
      return _isCurrentlyDst(ianaTimezone) ? (dst ?? std ?? '') : (std ?? '');
    }
    return abbrPart;
  }
  return label.match(/\(([A-Z]{2,6})\)/)?.[1] ?? label;
}

function _isCurrentlyDst(ianaTimezone: string): boolean {
  try {
    const year = new Date().getFullYear();
    const getOffsetH = (d: Date): number => {
      const raw = new Intl.DateTimeFormat('en-US', {
        timeZone: ianaTimezone, timeZoneName: 'shortOffset',
      }).formatToParts(d).find(p => p.type === 'timeZoneName')?.value ?? 'GMT+0';
      return parseInt(raw.match(/GMT([+-]\d+)/)?.[1] ?? '0', 10);
    };
    const jan = getOffsetH(new Date(Date.UTC(year, 0, 15)));
    const jul = getOffsetH(new Date(Date.UTC(year, 6, 15)));
    const now = getOffsetH(new Date());
    return jan !== jul && now === Math.max(jan, jul);
  } catch { return false; }
}
